import { useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { Tv, Copy, RefreshCw, Pencil, Power, Trash2, Eraser, MoreHorizontal } from 'lucide-react';
import { Container, PageHeader, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  Button,
  IconButton,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useChannelDetail,
  useFlushChannel,
  useResyncChannel,
  useUpdateChannel,
  useDeleteChannel,
} from '@/api/fastchannel';
import { channelM3u8Url, isChannelDisabled } from './shared';
import { ChannelStatusPill } from './ChannelStatusPill';
import { EditChannelDialog } from './EditChannelDialog';
import { ScheduleTimeline } from './schedule/ScheduleTimeline';
import { PreviewTab } from './PreviewTab';
import { AnalyticsTab } from './AnalyticsTab';

const BASE = '/services/fastchannel';

const TABS = [
  { value: 'schedules', label: 'Schedule' },
  { value: 'preview', label: 'Preview' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'library', label: 'Content Library' },
];

function ComingSoon({ title, description }: { title: string; description: string }) {
  return (
    <EmptyState icon={<Tv size={20} />} title={title} description={description} />
  );
}

export function ChannelDetail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceUuid, channelUuid } = useParams<{ serviceUuid: string; channelUuid: string }>();
  const { show } = useToast();
  const [flushOpen, setFlushOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: channel, isLoading } = useChannelDetail(serviceUuid, channelUuid);

  const channelBase = `${BASE}/${serviceUuid}/channels/${channelUuid}`;
  const seg = pathname.replace(channelBase, '').replace(/^\//, '').split('/')[0] || 'schedules';
  const name = channel?.name ?? '';
  const disabled = isChannelDisabled(channel?.status);

  // Re-sync = non-destructive: packager re-reads the current schedule now.
  const resync = useResyncChannel(serviceUuid ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Playout re-synced', description: 'Packager re-read the current schedule.' }),
    onError: () => show({ tone: 'danger', title: 'Re-sync failed' }),
  });
  // Flush = destructive: deletes ALL programs from the schedule.
  const flush = useFlushChannel(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Schedule cleared', description: 'All programs removed.' });
      setFlushOpen(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not clear schedule' }),
  });
  const updateChannel = useUpdateChannel(serviceUuid ?? '', channelUuid ?? '', {
    onSuccess: () => show({ tone: 'success', title: disabled ? 'Channel enabled' : 'Channel disabled' }),
    onError: () => show({ tone: 'danger', title: 'Could not update channel' }),
  });
  const removeChannel = useDeleteChannel(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Channel deleted' });
      navigate(`${BASE}/${serviceUuid}`);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete channel' }),
  });

  const copyM3u8 = () => {
    void navigator.clipboard?.writeText(channelM3u8Url(channelUuid));
    show({ tone: 'success', title: 'Stream URL copied' });
  };

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 36, width: 280, marginBottom: 'var(--vn-space-6)' }} />
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  if (!channel) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Tv size={20} />}
          title="Channel not found"
          description="It may have been removed, or you don't have access to it."
          actions={
            <Button variant="secondary" onClick={() => navigate(`${BASE}/${serviceUuid}`)}>
              Back to channels
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Tv size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>FastChannel</BreadcrumbItem>
            <BreadcrumbItem href={`${BASE}/${serviceUuid}`} onClick={(e) => { e.preventDefault(); navigate(`${BASE}/${serviceUuid}`); }}>Channels</BreadcrumbItem>
            <BreadcrumbItem current>{channel.title || channel.name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={channel.title || channel.name}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <ChannelStatusPill status={channel.status} />
            <Button
              variant="secondary"
              startIcon={<Power size={15} />}
              disabled={updateChannel.isPending}
              onClick={() => updateChannel.mutate({ status: disabled ? 'active' : 'inactive' })}
            >
              {disabled ? 'Enable' : 'Disable'}
            </Button>
            <Button variant="secondary" startIcon={<Pencil size={15} />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" aria-label="More channel actions">
                  <MoreHorizontal size={18} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={copyM3u8}>
                  <Copy size={14} /> Copy stream URL
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={resync.isPending}
                  onSelect={() => channelUuid && resync.mutate(channelUuid)}
                >
                  <RefreshCw size={14} /> Re-sync playout
                </DropdownMenuItem>
                <DropdownMenuItem tone="danger" onSelect={() => setFlushOpen(true)}>
                  <Eraser size={14} /> Clear schedule
                </DropdownMenuItem>
                <DropdownMenuItem tone="danger" onSelect={() => setDeleteOpen(true)}>
                  <Trash2 size={14} /> Delete channel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <Tabs
        value={seg}
        onValueChange={(v) => navigate(v === 'schedules' ? `${channelBase}/schedules` : `${channelBase}/${v}`)}
        style={{ marginBottom: 'var(--vn-space-4)' }}
      >
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Routes>
        <Route index element={<Navigate to="schedules" replace />} />
        <Route
          path="schedules"
          element={<ScheduleTimeline serviceUuid={serviceUuid!} channelUuid={channelUuid!} channel={channel} />}
        />
        <Route path="preview" element={<PreviewTab channel={channel} />} />
        <Route path="analytics" element={<AnalyticsTab channel={channel} />} />
        <Route
          path="library"
          element={
            <ComingSoon
              title="Content Library coming soon"
              description="A centralized media library for this channel is under development."
            />
          }
        />
        <Route path="*" element={<Navigate to="schedules" replace />} />
      </Routes>

      <ConfirmDialog
        open={flushOpen}
        onOpenChange={(o) => !o && setFlushOpen(false)}
        title="Clear all programs?"
        description={`This permanently DELETES every program in “${channel.title || name}”’s schedule. Playout falls back to the slate until you add programs again. This cannot be undone — it is not a re-sync.`}
        confirmLabel="Clear schedule"
        destructive
        requireText={name}
        loading={flush.isPending}
        onConfirm={() => channelUuid && flush.mutate(channelUuid)}
      />

      <EditChannelDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        serviceUuid={serviceUuid!}
        channel={channel}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => !o && setDeleteOpen(false)}
        title="Delete channel?"
        description={`This permanently removes “${channel.title || name}” and all its schedules. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={name}
        loading={removeChannel.isPending}
        onConfirm={() => channelUuid && removeChannel.mutate(channelUuid)}
      />
    </Container>
  );
}

